










































import {Component, Prop, Provide, Vue, Watch} from 'vue-property-decorator';
import FlexGridModel, {EditDialogsConfig, Item} from '@/models/FlexGridModel';
import FlexGridHeader from './header/FlexGridHeader.vue';
import FlexGridFooter from './footer/FlexGridFooter.vue';
import FlexGridTable from './table/FlexGridTable.vue';
import FlexGridEditTypeEnum from '@/enums/FlexGridEditTypeEnum';
import axios from 'axios';
import CDeleteDialog from '@/components/base/dialog/CDeleteDialog.vue';
import {cloneDeep} from 'lodash';
import FlexGridSortingTypeEnum from '@/enums/FlexGridSortingTypeEnum';
import FlexGridSettingsEditDialog from '@/components/base/flex-grid/header/FlexGridDisplaySettingsEditDialog.vue';
import FlexGridModule from '@/store/modules/flexGridModule';
import {
    SET_DATA,
    SET_LOCAL_ID_GRID_CONFIG_ITEM,
    SET_SORT_INDEX,
    SET_SORT_KEY,
    SET_SORT_TYPE,
    SET_SORTED_DATA,
    SET_COMPRESSED,
    UPDATE_CONFIGS,
    UPDATE_IMPORT_CONFIGS,
    UPDATE_CONFIG,
    SET_IS_EDITABLE,
} from '@/store/modules/flexGridModule/flexGrid-mutation-types';
import retryTimes = jest.retryTimes;

/**
 * Main flex table component
 * @requires ./header/FlexGridHeader.vue
 * @requires ./footer/FlexGridFooter.vue
 * @requires ./table/FlexGridTable.vue
 * @requires ./header/FlexGridDisplaySettingsEditDialog.vue
 */
@Component({
    components: {
        FlexGridSettingsEditDialog,
        CDeleteDialog,
        FlexGridTable,
        FlexGridHeader,
        FlexGridFooter,
    },
})
export default class CFlexGrid extends Vue {
    /**
     * Url path using in edit and settings requests
     */
    @Provide()
    @Prop({
        required: true,
    })
    urlPath!: string;
    /**
     * Url query using in edit and settings requests
     */
    @Provide()
    @Prop({default: () => ({})})
    urlQuery!: {
        [key: string]: string | number;
    };
    /**
     * Title for flex-grid, rendering in header
     */
    @Prop() title!: string;
    /**
     * Flag using to apply subcomponents styles
     */
    @Prop({type: Boolean, default: false}) isSubcomponent!: boolean;
    /**
     * Vue component to using as subcomponent
     */
    @Provide()
    @Prop()
    subcomponent!: Object | undefined;
    /**
     * Activate sort feature
     */
    @Provide()
    @Prop({type: Boolean, default: true})
    sortable!: boolean;
    /**
     * Flag defines should zeros render in rows or not
     */
    @Provide()
    @Prop({type: Boolean, default: false})
    showZeros!: boolean;
    /**
     * Activate edit feature, (add, edit, copy, delete, delete all, etc.)
     */
    @Provide()
    @Prop({type: Boolean, default: undefined})
    editable!: boolean | undefined;
    /**
     * Vue component. It should be defined, if editable flag activate
     */
    @Prop() editDialog!: Object;
    /**
     * Min-width and width of edit actions column
     */
    @Provide()
    @Prop({
        type: String,
        default: '125px',
    })
    editBtnCellMinWidth!: string;
    /**
     * Config defines multiple edit dialogs (e.g 'Equipment type' catalogue)
     */
    @Provide()
    @Prop()
    editDialogsConfig!: EditDialogsConfig | undefined;
    /**
     * If prop defined, setting change in flex-grid do not remember
     */
    @Prop() idGridConfigItem!: number;
    @Provide()
    @Prop({default: false, type: Boolean})
    selectable!: boolean;
    /**
     * If prop set to true, flex-grid well be compressed
     */
    @Prop({default: false, type: Boolean}) isCompressed!: boolean;
    @Prop() editDialogProps!: {[key: string]: any};
    /**
     * If true, hide copy action button
     */
    @Provide()
    @Prop({type: Boolean, default: false})
    hideCopyButton!: boolean;

    @Provide()
    @Prop()
    deleteDisableFn!: Function;

    private _uid!: number;
    @Provide()
    private moduleName!: string;

    private loading: boolean = false;

    private sortKeyType: string | null = null;

    @Provide()
    get isIdGridConfigItemFromSettings() {
        return !!this.idGridConfigItem;
    }

    get mainData(): FlexGridModel | null {
        return this.$store.state[this.moduleName].data;
    }

    get sortKey(): string | null {
        return this.$store.state[this.moduleName].sortKey;
    }

    get sortIndex(): number | null {
        return this.$store.state[this.moduleName].sortIndex;
    }

    get sortType(): FlexGridSortingTypeEnum {
        return this.$store.state[this.moduleName].sortType;
    }

    get compressed(): boolean | undefined {
        return this.$store.state[this.moduleName].compressed;
    }

    get localIdGridConfigItem(): number | undefined {
        return this.$store.state[this.moduleName].localIdGridConfigItem;
    }

    get sortedData(): FlexGridModel | null {
        return this.$store.state[this.moduleName].sortedData;
    }

    get transposeLoading(): boolean {
        return this.$store.state[this.moduleName].transposeLoading;
    }

    @Watch('mainData')
    onMainDataChange(value: FlexGridModel, oldValue: FlexGridModel | null) {
        // if get data first time
        if (oldValue === null) {
            const bool = this.editable !== undefined ? this.editable : value.Config.IsEditable;
            this.commit(SET_IS_EDITABLE, bool);
        }
        const result = this.getSortedData(value);
        this.commit(SET_SORTED_DATA, result);
    }

    beforeCreate() {
        this.moduleName = `flex-grid${this._uid}`;
        this.$store.registerModule(this.moduleName, FlexGridModule);
    }

    created() {
        this.getData();
    }

    updated() {
        if (this.compressed === undefined) this.commit(SET_COMPRESSED, this.isCompressed);
    }

    destroyed() {
        this.$store.unregisterModule(this.moduleName);
    }

    @Provide()
    commit(mutation: string, payload: any) {
        this.$store.commit(`${this.moduleName}/${mutation}`, payload);
    }

    /**
     * @public
     */
    async getData() {
        this.loading = true;
        try {
            const {data} = await this.$http.get(`${this.urlPath}/GetAll`, {
                params: {
                    ...this.urlQuery,
                    idGridConfigItem: this.localIdGridConfigItem,
                },
            });
            this.commit(SET_DATA, data);
        } finally {
            this.loading = false;
        }
    }

    add(id: number) {
        this.openEditDialog(FlexGridEditTypeEnum.Add, {id});
    }
    edit(item: any) {
        this.openEditDialog(FlexGridEditTypeEnum.Edit, item);
    }
    copy(item: any) {
        this.openEditDialog(FlexGridEditTypeEnum.Copy, item);
    }
    deleteOne(item: any) {
        const {url, query} = this.getEditOptions(item);
        (this.$refs.deleteDialog as any).open(item.Id, {
            deleteCallback: async (id: number) => {
                try {
                    const {data} = await this.$http.post(`${url}/Delete`, null, {
                        params: {
                            ...query,
                            id,
                        },
                    });
                    if (data.Dependencies) (this.$refs.deleteDialog as any).error(data.Dependencies);
                    else {
                        this.commit(SET_DATA, data);
                        (this.$refs.deleteDialog as any).close();
                    }
                } catch (err) {
                    (this.$refs.deleteDialog as any).close();
                }
            },
        });
    }
    deleteAll() {
        (this.$refs.deleteDialog as any).open(null, {
            deleteCallback: async () => {
                try {
                    const {data} = await this.$http.post(`${this.urlPath}/DeleteAll`, null, {
                        params: {
                            ...this.urlQuery,
                        },
                    });
                    if (data.Dependencies) (this.$refs.deleteDialog as any).error(data.Dependencies);
                    else {
                        this.commit(SET_DATA, data);
                        (this.$refs.deleteDialog as any).close();
                    }
                } catch (err) {
                    (this.$refs.deleteDialog as any).close();
                }
            },
        });
    }
    openEditDialog(actionType: FlexGridEditTypeEnum, item: any) {
        const {type, title, urlPart} = this.getInitialParams(actionType);
        const {url, query, dialog} = this.getEditOptions(item);
        const options = {
            url,
            query,
            type,
            title,
            saveCallback: (data: any) => {
                this.save(data, {
                    dialog,
                    url,
                    query,
                });
            },
        };
        const CancelToken = axios.CancelToken,
            source = CancelToken.source();
        const request = this.$http.get(`${url}${urlPart}`, {
            params: {
                ...query,
            },
            cancelToken: source.token,
        });
        dialog.openAsync(request, source, options);
    }
    getEditOptions(item: any) {
        let url: string, query: any, dialog: any;
        if (this.editDialogsConfig) {
            const editConfigId = this.editDialogsConfig.getId(item);
            const tmp = this.editDialogsConfig.items.find(item => item.id === editConfigId);
            if (tmp) {
                url = tmp.urlPath;
                query = {
                    id: item.id || item.Id,
                    ...(this.editDialogsConfig ? {returnAllTypes: true} : undefined),
                    ...this.urlQuery,
                };
                dialog = (this.$refs[tmp.dialogRef] as any)[0];
            } else throw Error('edit dialogs config error');
        } else {
            url = this.urlPath;
            query = {
                ...this.urlQuery,
                id: item.Id,
            };
            dialog = this.$refs.editDialog as any;
        }
        return {
            url,
            query,
            dialog,
        };
    }
    getInitialParams(actionType: FlexGridEditTypeEnum) {
        let params;
        switch (actionType) {
            case FlexGridEditTypeEnum.Add:
                params = {
                    type: 'add',
                    title: this.$t('Common.Add'),
                    urlPart: '/GetCreate',
                };
                break;
            case FlexGridEditTypeEnum.Edit:
                params = {
                    type: 'edit',
                    title: this.$t('Common.Edit'),
                    urlPart: '/GetEdit',
                };
                break;
            case FlexGridEditTypeEnum.Copy:
                params = {
                    type: 'copy',
                    title: this.$t('Common.Copy'),
                    urlPart: '/GetCopy',
                };
                break;
        }
        return params;
    }
    async save(
        payload: any,
        options: {
            dialog: any;
            url: string;
            query: {[key: string]: any};
        },
    ) {
        try {
            const {data} = await this.$http.post(`${options.url}/Save`, payload, {
                params: {
                    ...options.query,
                },
                is400ErrorHandled: true,
            } as any);
            this.commit(SET_DATA, data);
            options.dialog.close();
        } catch (e) {
            if (e?.response.status === 400) {
                options.dialog.setupErrors(e.response.data.errors);
            }
        }
    }
    onSortBy(data: {key: string; type: string; index?: number}) {
        if (this.sortKey === data.key && this.sortIndex == data.index) {
            if (this.sortType === FlexGridSortingTypeEnum.Down) {
                this.commit(SET_SORT_KEY, null);
                this.sortKeyType = null;
                this.commit(SET_SORT_TYPE, FlexGridSortingTypeEnum.Up);
            } else {
                this.commit(SET_SORT_TYPE, FlexGridSortingTypeEnum.Down);
            }
        } else {
            this.commit(SET_SORT_KEY, data.key);
            this.sortKeyType = data.type;
            this.commit(SET_SORT_TYPE, FlexGridSortingTypeEnum.Up);
        }
        if (typeof data.index === 'number') this.commit(SET_SORT_INDEX, data.index);
        else this.commit(SET_SORT_INDEX, null);

        const result = this.getSortedData(this.mainData);
        this.commit(SET_SORTED_DATA, result);
    }
    sort(array: any) {
        array.sort((a: any, b: any) => {
            const value1 =
                typeof this.sortIndex === 'number'
                    ? a[this.sortKey as string][this.sortIndex]
                    : a[this.sortKey as string];

            const value2 =
                typeof this.sortIndex === 'number'
                    ? b[this.sortKey as string][this.sortIndex]
                    : b[this.sortKey as string];

            if (this.sortKeyType === 'Decimal' || this.sortKeyType === 'Int32') {
                if (this.showZeros) {
                    if (value1 === null && value2 !== null) return 1;
                    if (value1 !== null && value2 === null) return -1;
                } else {
                    if (!value1 && value2) return 1;
                    if (value1 && !value2) return -1;
                }
                if (this.sortType === FlexGridSortingTypeEnum.Up) return value1 - value2;
                else return value2 - value1;
            } else if (this.sortKeyType === 'String') {
                const collator = new Intl.Collator();
                if (this.sortType === FlexGridSortingTypeEnum.Up) return collator.compare(value1, value2);
                else if (this.sortType === FlexGridSortingTypeEnum.Down) return collator.compare(value2, value1);
            } else {
                if (this.sortType === FlexGridSortingTypeEnum.Up) return value1 - value2;
                else return value2 - value1;
            }
        });
    }
    onCurrentFlexGridSettingEdit() {
        const options = {
            type: 'edit',
            title: this.$t('Common.Edit'),
            saveCallback: (data: any) => {
                this.onCurrentFlexGridSettingSave(data);
            },
        };
        const CancelToken = axios.CancelToken,
            source = CancelToken.source();
        const request = this.$http.get(`${this.urlPath}/GetConfigEdit`, {
            params: {
                idGridConfigItem: this.mainData?.Config.IdCurrentConfigItem,
                ...this.urlQuery,
            },
            cancelToken: source.token,
        });
        (this.$refs.settingsDialog as any).openAsync(request, source, options);
    }
    async onCurrentFlexGridSettingSave(payload: any) {
        try {
            const {data: configs} = await this.$http.post(`${this.urlPath}/SaveConfig`, payload, {
                params: {
                    ...this.urlQuery,
                },
            });
            this.getData();
        } finally {
            (this.$refs.settingsDialog as any).close();
        }
    }
    async onActivateFlexGridSetting(id: number) {
        if (id === this.mainData?.Config.IdCurrentConfigItem) return;
        if (this.idGridConfigItem) this.commit(SET_LOCAL_ID_GRID_CONFIG_ITEM, id);
        else
            await this.$http.post(`${this.urlPath}/SetCurrentConfig`, null, {
                params: {
                    ...this.urlQuery,
                    idGridConfigItem: id,
                },
            });
        this.getData();
    }

    /**
     * @public
     * Method to update data from parent in case where setups additional edit buttons
     */
    updateData(data: FlexGridModel) {
        this.commit(SET_DATA, data);
    }
    onCompressedClick() {
        this.commit(SET_COMPRESSED, !this.compressed);
    }
    onUpdateConfigs(event: any) {
        this.commit(UPDATE_CONFIGS, event);
    }
    onUpdateImportConfigs(event: any) {
        this.commit(UPDATE_IMPORT_CONFIGS, event);
    }
    onUpdateConfig(event: any) {
        this.commit(UPDATE_CONFIG, event);
    }
    getSortedData(data: FlexGridModel | null) {
        if (!data) return null;
        const result = cloneDeep(data);
        if (this.sortKey && this.sortType) {
            const callback = (item: Item, index: number, array: Array<Item>) => {
                if (item.Items) item.Items.forEach(callback);
                else {
                    if (index === 0) this.sort(array);
                }
            };
            result?.Data.Items.forEach(callback);
        }
        return result;
    }

    /**
     * @public
     */
    getSelectedList() {
        return this.$store.state[this.moduleName].selectedList;
    }
}
